import { useEffect, useRef } from "react";
import "./CrushClawAnimation.css";

export default function CrushClawAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for defender
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate center of defender sprite
    const frameWidth = 192;  // Width of each frame (960px total ÷ 5 frames)
    const frameHeight = 192; // Height of the sprite
    const centerX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const centerY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Initial attacker animation (slight scale before attack)
    attackerEl.style.transition = 'transform 0.2s ease-in-out';
    attackerEl.style.transform = 'scale(1.1)';
    
    // Create and position animation element
    const crushClaw = document.createElement("div");
    crushClaw.className = "crush-claw-anim";
    crushClaw.style.setProperty('--sx', `${centerX - frameWidth / 2}px`);
    crushClaw.style.setProperty('--sy', `${centerY - frameHeight / 2}px`);
    container.appendChild(crushClaw);
    animRef.current = crushClaw;
    
    // Create crush effects at impact moment (frame 3)
    const crushEffectTimer = setTimeout(() => {
      // Crush flash effect
      const flash = document.createElement('div');
      flash.className = 'crush-claw-flash';
      flash.style.left = `${centerX}px`;
      flash.style.top = `${centerY}px`;
      container.appendChild(flash);
      
      // Make defender react
      defenderEl.classList.add('crush-claw-hit');
      
      // Remove flash and reset defender
      setTimeout(() => {
        flash.remove();
        defenderEl.classList.remove('crush-claw-hit');
      }, 300);
      
      // Create crushing particles
      for (let i = 0; i < 14; i++) {
        const particle = document.createElement('div');
        particle.className = 'crush-claw-particle';
        
        // Position around the impact point
        const angle = Math.random() * Math.PI * 2;
        const distance = 5 + Math.random() * 35;
        particle.style.left = `${centerX + Math.cos(angle) * distance}px`;
        particle.style.top = `${centerY + Math.sin(angle) * distance}px`;
        
        // Random rotation and scale
        particle.style.setProperty('--rotation', `${Math.random() * 360}deg`);
        particle.style.setProperty('--scale', `${0.8 + Math.random() * 0.9}`);
        
        container.appendChild(particle);
        
        // Auto-remove particles
        setTimeout(() => particle.remove(), 700 + Math.random() * 400);
      }
      
      // Create crush claw marks
      const clawMark = document.createElement('div');
      clawMark.className = 'crush-claw-mark';
      clawMark.style.left = `${centerX}px`;
      clawMark.style.top = `${centerY}px`;
      container.appendChild(clawMark);
      
      // Auto-remove claw mark
      setTimeout(() => clawMark.remove(), 1000);
      
      // Defense reduction chance (50% chance)
      if (Math.random() < 0.5) {
        // Enhanced crushing effect for defense reduction
        flash.classList.add('crush-claw-defense-reduction');
        
        // Add defense reduction aura to opponent
        defenderEl.classList.add('crush-claw-defense-debuff');
        
        // Remove defense debuff after animation
        setTimeout(() => {
          if (defenderEl) {
            defenderEl.classList.remove('crush-claw-defense-debuff');
          }
        }, 800);
      }
      
    }, 350); // Sync with frame 3 impact

    // Reset attacker position after animation
    const resetTimer = setTimeout(() => {
      attackerEl.style.transition = 'transform 0.2s ease-out';
      attackerEl.style.transform = '';
    }, 600);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      if (crushClaw.parentNode) {
        crushClaw.remove();
      }
      onComplete();
    }, 1000);

    // Cleanup function
    return () => {
      clearTimeout(crushEffectTimer);
      clearTimeout(resetTimer);
      clearTimeout(completeTimer);
      
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
      }
      
      if (defenderEl) {
        defenderEl.classList.remove('crush-claw-hit', 'crush-claw-defense-debuff');
      }
      
      if (crushClaw.parentNode) {
        crushClaw.remove();
      }
      
      // Remove any flash, particles or marks
      const elements = container.querySelectorAll('.crush-claw-flash, .crush-claw-particle, .crush-claw-mark');
      elements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set the move type for proper coloring in the battle UI
CrushClawAnimation.moveType = "normal";
